import type { Call } from './types';

// A pending recording this old is not "uploading" any more: the phone is offline,
// limited to Wi-Fi uploads or out of storage.
const STALE_UPLOAD_MS = 6 * 3_600_000;

export interface RecordingState {
  label: string;
  tone: 'neutral' | 'warning' | 'muted';
  title: string;
}

/** What the Recording cell says when there is nothing to play, or null when there is. */
export function recordingState(
  call: Pick<Call, 'fileStatus' | 'recordingUrl' | 'createdAt' | 'callTime' | 'duration'>,
  now = Date.now(),
): RecordingState | null {
  if (call.recordingUrl) return null;
  const status = call.fileStatus || '';
  if (status === 'pending' || status === 'uploading') {
    const since = Date.parse(call.createdAt || call.callTime || '');
    return Number.isFinite(since) && now - since > STALE_UPLOAD_MS
      ? {
          label: 'Not uploaded yet',
          tone: 'warning',
          title: 'The phone hasn’t uploaded this recording. It may be offline, limited to Wi-Fi uploads or out of storage.',
        }
      : { label: 'Uploading', tone: 'neutral', title: 'The phone is uploading this recording.' };
  }
  switch (status) {
    case 'failed':
    case 'error':
      return { label: 'Upload failed', tone: 'warning', title: 'The phone couldn’t upload this recording.' };
    case 'not_found':
      return {
        label: 'Not found on phone',
        tone: 'muted',
        title: 'The phone has no recording for this call. Check that call recording is on in its Phone app.',
      };
    case 'not_allowed':
      return { label: 'Recording off', tone: 'muted', title: 'Recording was off on the phone for this call.' };
    case 'disabled':
      return {
        label: 'Not uploaded',
        tone: 'muted',
        title:
          'Recording uploads are off for this person, paused by the plan or storage, or the contact is excluded.',
      };
    case 'deleted':
      return { label: 'Deleted', tone: 'muted', title: 'This recording was deleted.' };
    default:
      return {
        label: 'No recording',
        tone: 'muted',
        title: call.duration ? 'No recording reached the dashboard for this call.' : 'The call didn’t connect.',
      };
  }
}
